import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../utils/formatDate';
import axios from 'axios';
import { API_URL } from '../config';

const Home = () => {
  const [blogs, setBlogs] = useState([]);
  const [services, setServices] = useState([]);
  const [hero, setHero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([getBlogs(), getServices(), getHero()]);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Otomatik kaydırma efekti
  useEffect(() => {
    const interval = setInterval(() => {
      setScrollPosition((prev) => (prev + 1) % services.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [services.length]);

  // Görüntülenecek kartları hesapla
  const getVisibleServices = () => {
    if (!Array.isArray(services) || services.length === 0) {
      console.log('No services available');
      return [];
    }
    
    console.log('Current services:', services);
    console.log('Current scroll position:', scrollPosition);
    
    const result = [];
    for (let i = 0; i < 3 && i < services.length; i++) {
      const index = (scrollPosition + i) % services.length;
      result.push(services[index]);
    }
    
    console.log('Visible services:', result);
    return result;
  };

  const getBlogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blogs?populate=*`);
      const transformedBlogs = response.data.data.map(blog => {
        return {
          id: blog.id,
          title: blog.title || '',
          content: blog.content || '',
          image: blog.image?.url ? `${API_URL}${blog.image.url}` : null,
          category: blog.category || null,
          createdAt: blog.createdAt || null
        };
      });
      console.log('Transformed Blogs:', transformedBlogs);
      setBlogs(transformedBlogs);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setError('Blog yazıları yüklenirken bir hata oluştu.');
    }
  };

  const getServices = async () => {
    try {
      const API_URL_SERVICES = `${API_URL}/api/services?populate=*&sort[0]=order:asc`;
      const response = await axios.get(API_URL_SERVICES);
      console.log('API Response:', response.data);
      
      if (!response.data?.data) {
        console.error('Invalid API response structure:', response.data);
        setServices([]);
        return;
      }

      const transformedServices = response.data.data.map(service => {
        console.log('Service:', service);
        return {
          id: service.documentId,
          title: service.title || '',
          description: service.description || '',
          image: service.image?.formats?.small?.url 
            ? `${API_URL}${service.image.formats.small.url}` 
            : null,
          order: service.order || 0
        };
      });

      console.log('Final Transformed Services:', transformedServices);
      setServices(transformedServices);
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
    }
  };

  const getHero = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/heroes?populate=*`);
      console.log('Hero API Response:', response.data);
      
      if (response.data.data && response.data.data[0]) {
        const heroData = response.data.data[0];
        setHero({
          title: heroData.title || '',
          subtitle: heroData.subtitle || '',
          image: heroData.image?.formats?.large?.url ? 
            `${API_URL}${heroData.image.formats.large.url}` : null
        });
      }
    } catch (error) {
      console.error('Hero verisi alınırken hata:', error);
      setError('Hero bölümü yüklenirken bir hata oluştu.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 pt-20">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section */}
      {hero && (
        <div className="relative h-[80vh] overflow-hidden">
          {hero.image && (
            <div className="absolute inset-0">
              <img
                src={hero.image}
                alt={hero.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black opacity-50"></div>
            </div>
          )}
          <div className="relative container mx-auto px-4 h-full flex items-center">
            <div className="max-w-3xl text-white">
              <h1 className="text-5xl font-bold mb-4">{hero.title}</h1>
              <p className="text-xl">{hero.subtitle}</p>
            </div>
          </div>
        </div>
      )}

      {/* Çalışma Alanları Section */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">Çalışma Alanları</h2>
          </div>
          
          {/* Services Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-10">
            {getVisibleServices().map((service) => (
              <Link 
                key={service.id}
                to={`/services/${service.id}`}
                className="transform transition-all duration-300 hover:-translate-y-1"
              >
                <div className="border-2 border-gray-200 rounded-lg hover:border-primary transition-colors duration-300 overflow-hidden">
                  {/* Image */}
                  {service.image && (
                    <div className="h-40 sm:h-48 overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="flex flex-col items-center p-4 sm:p-6 text-center">
                    {/* Title */}
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Tümünü Gör Button */}
          <div className="text-center">
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-primary text-primary font-medium rounded-md hover:bg-primary hover:text-white transition-colors group"
            >
              Tüm Çalışma Alanlarını Gör
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 ml-2 transform transition-transform group-hover:translate-x-1" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Blog Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8">Blog</h2>
          <div className="overflow-x-auto pb-4 hide-scrollbar">
            <div className="flex gap-8 min-w-max">
              {blogs.slice(0, 6).map((blog) => (
                <Link 
                  key={blog.id} 
                  to={`/blog/${blog.id}`} 
                  className="group w-[350px]"
                  onClick={() => window.scrollTo(0, 0)}
                >
                  <div className="border-2 border-gray-200 rounded-lg hover:border-primary transition-colors duration-300 h-[400px] flex flex-col overflow-hidden">
                    {blog.image && (
                      <div className="h-48 overflow-hidden flex-shrink-0">
                        <img
                          src={blog.image}
                          alt={blog.title}
                          className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-grow">
                      {blog?.category?.name && (
                        <span className="inline-block px-2 py-0.5 text-xs font-medium text-primary bg-primary/10 rounded-full mb-2 w-fit">
                          {blog.category.name}
                        </span>
                      )}
                      <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {blog.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">{blog.content}</p>
                      <div className="text-sm text-gray-500 mt-auto">
                        {blog.createdAt && formatDate(blog.createdAt)}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="text-center mt-10">
            <Link
              to="/blog"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-primary text-primary font-medium rounded-md hover:bg-primary hover:text-white transition-colors group"
              onClick={() => window.scrollTo(0, 0)}
            >
              Tüm Blog Yazılarını Gör
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5 ml-2 transform transition-transform group-hover:translate-x-1" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
